// src/components/MyStories/MyStories.tsx
import React, { useState, useEffect, useRef, useContext } from 'react';
import { StoryContext } from '../../contexts/StoryContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useGuestMode } from '../../hooks/useGuestMode';
import { RegistrationGate } from '../auth/RegistrationGate';
import { StoryService, StoryData } from '../../services/storyService';
import CoverRenderer from '../Storybook/CoverRenderer';
import { Filter } from 'bad-words';
import { Timestamp } from 'firebase/firestore';
import { doc, getDoc, setDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../../firebase/firebase';
import Notification from '../Account/Notification';
import GuestModeManager from '../../firebase/guestModeManager';
import { getGuestStories } from '../../utils/guestStoryStorage';
import { useSafetyCheck, SafetyIssue } from '../../hooks/useSafetyCheck';  // adjust path
import SafetyFeedbackModal from '../Safety/SafetyFeedbackModal';          // adjust path
import { getFunctions, httpsCallable } from 'firebase/functions';
 
// ── Design tokens ─────────────────────────────────────────────────────────────
const C = {
    primary:            '#6a37d4',
    primaryLight:       '#ae8dff',
    primaryContainer:   '#ede8ff',
    secondary:          '#006947',
    secondaryContainer: '#d1fae5',
    tertiary:           '#6f5900',
    tertiaryContainer:  '#fed01b',
    surface:            '#f3f6ff',
    surfaceLow:         '#eaf1ff',
    surfaceHigh:        '#d6e3f9',
    surfaceHighest:     '#cfdef5',
    surfaceLowest:      '#ffffff',
    onBackground:       '#252f3c',
    onSurface:          '#252f3c',
    onSurfaceVariant:   '#525c6a',
    outlineVariant:     '#a4aebe',
    error:              '#b41340',
    errorContainer:     '#ffd9df',
};
 
const SIDEBAR_WIDTH = 220;
 
const MyStories: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { currentUser } = useAuth();
    const { isGuestMode } = useGuestMode();
    const [showPublishGate, setShowPublishGate] = useState(false);
    const username = currentUser?.displayName || 'Writer';
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const { markAsDeleted, isDeleted } = useContext(StoryContext);
 
    const isActive = (path: string) => location.pathname === path;
    const [activeTab, setActiveTab] = useState<'class' | 'personal'>('personal');
    const [classFilter, setClassFilter] = useState<'all' | 'drafts' | 'submitted' | 'returned'>('all');
    const [personalFilter, setPersonalFilter] = useState<'all' | 'drafts' | 'published' | 'practice'>('all');
    const [stories, setStories] = useState<StoryData[]>([]);
    const [challengeStories, setChallengeStories] = useState<StoryData[]>([]);
    const [loading, setLoading] = useState(true);
 
    const [deleteModal, setDeleteModal] = useState<StoryData | null>(null);
    const [editTagsModal, setEditTagsModal] = useState<StoryData | null>(null);
    const [editingTags, setEditingTags] = useState<string[]>([]);
    const [confirmModal, setConfirmModal] = useState<{
        show: boolean; type: 'publish' | 'unpublish' | 'republish'; story: StoryData | null;
    }>({ show: false, type: 'publish', story: null });
 
    const [notification, setNotification] = useState<{
      show: boolean; type: 'save' | 'publish' | 'unpublish' | 'republish' | 'tags' | 'error' | 'profanity' | 'moderation' | 'story-submitted';
      storyTitle?: string; errorMessage?: string; className?: string;
  }>({ show: false, type: 'save' });
 
    const availableTags = [
        '🐉 Fantasy', '😂 Funny', '🏃 Adventure', '🔍 Mystery',
        '🚀 Sci-Fi', '❤️ Friendship', '🐾 Animals', '🏫 School',
        '👨‍👩‍👧 Family', '⚽ Sports', '🎨 Creative', '😱 Scary',
    ];
 
    const MAX_PUBLISHED_STORIES = 10;
    const MIN_WORD_COUNT = 350;
    const MAX_PUBLISH_PER_MONTH = 3;
    const hasLoadedRef = useRef(false);
 
    // ── Data loading (unchanged) ──────────────────────────────────────────────
    useEffect(() => {
        const loadStories = async () => {
            setLoading(true);
            try {
                const userStories = await StoryService.getUserStories(currentUser?.uid || null);
                const regular = userStories.filter(s => !s.isChallenge && !isDeleted(s.id || ''));
                const challenges = userStories.filter(s => s.isChallenge);
                setStories(regular);
                setChallengeStories(challenges);
            } catch (error) {
                console.error('Error loading stories:', error);
            }
            setLoading(false);
        };
        loadStories();
    }, [isDeleted]);
 
    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);
 
    // ── Helpers (all unchanged) ───────────────────────────────────────────────
    const countWords = (story: StoryData): number => {
      if (!story.content?.chapters) return 0;
      return story.content.chapters.reduce((total: number, chapter: any) => {
          // Block-based format
          if (chapter.blocks) {
              return total + chapter.blocks.reduce((t: number, block: any) => {
                  if (block.type !== 'text' || !block.content) return t;
                  const text = block.content.replace(/<[^>]*>/g, '');
                  return t + text.trim().split(/\s+/).filter((w: string) => w.length > 0).length;
              }, 0);
          }
          // Legacy format
          const text = chapter.content?.replace(/<[^>]*>/g, '') || '';
          return total + text.trim().split(/\s+/).filter((w: string) => w).length;
      }, 0);
  };
 
    const getTimeAgo = (timestamp: any): string => {
        if (!timestamp) return 'Recently';
        const date = timestamp instanceof Timestamp ? timestamp.toDate() : new Date(timestamp);
        const diffMs = Date.now() - date.getTime();
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);
        if (diffMins < 60) return diffMins <= 1 ? 'Just now' : `${diffMins}m ago`;
        if (diffHours < 24) return diffHours === 1 ? '1h ago' : `${diffHours}h ago`;
        if (diffDays === 1) return 'Yesterday';
        if (diffDays < 7) return `${diffDays}d ago`;
        return `${Math.floor(diffDays / 7)}w ago`;
    };
 
    const getPublishesThisMonth = (stories: StoryData[]): number => {
        const firstDayOfMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
        return stories.filter(story => {
            if (!story.publishedAt) return false;
            const d = story.publishedAt instanceof Timestamp ? story.publishedAt.toDate() : new Date(story.publishedAt);
            return d >= firstDayOfMonth && story.isPublished;
        }).length;
    };
 
    const getStoryIcon = (type: string) => ({ 'regular-book': '📖', 'graphic-novel': '🎨', 'picture-book': '📚' }[type] || '📝');
 
    const getBackgroundStyle = (bg: any) => {
        if (!bg) return {};
        if (bg.type === 'solid') return { background: bg.value };
        if (bg.type === 'gradient') return { background: bg.value };
        if (bg.type === 'image') return { backgroundImage: `url(${bg.value})`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat' };
        if (bg.type === 'pattern') {
            const patterns: any = {
                'Stars': { value: 'radial-gradient(circle, #FFD700 1px, transparent 1px)', size: '20px 20px' },
                'Dots':  { value: 'radial-gradient(circle, rgba(255,255,255,0.3) 2px, transparent 2px)', size: '15px 15px' },
                'Stripes': { value: 'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(255,255,255,0.1) 10px, rgba(255,255,255,0.1) 20px)' },
            };
            const p = patterns[bg.value];
            return { background: '#6C5CE7', backgroundImage: p?.value, backgroundSize: p?.size };
        }
        return { background: '#6C5CE7' };
    };
 
    const getStoryRoute = (type: string) => ({ 'regular-book': '/regular-book-editor', 'graphic-novel': '/graphic-novel-editor', 'picture-book': '/picture-book-editor' }[type] || '/regular-book-editor');
 
    // ── Actions (all unchanged) ───────────────────────────────────────────────
    const toggleStar = async (e: React.MouseEvent, storyId: string) => {
        e.stopPropagation();
        const all = [...stories, ...challengeStories];
        const story = all.find(s => s.id === storyId);
        if (!story || !currentUser?.uid || !story.id) return;
        const newVal = !story.starred;
        story.isChallenge ? setChallengeStories(p => p.map(s => s.id === storyId ? { ...s, starred: newVal } : s)) : setStories(p => p.map(s => s.id === storyId ? { ...s, starred: newVal } : s));
        try { await StoryService.updateStory(currentUser.uid, story.id, { starred: newVal }); }
        catch { story.isChallenge ? setChallengeStories(p => p.map(s => s.id === storyId ? { ...s, starred: !newVal } : s)) : setStories(p => p.map(s => s.id === storyId ? { ...s, starred: !newVal } : s)); }
    };
 
    const handleDelete = (e: React.MouseEvent, story: StoryData) => { e.stopPropagation(); setDeleteModal(story); };
 
    const confirmDelete = async () => {
        if (!deleteModal?.id) return;
        const id = deleteModal.id; const isChallenge = deleteModal.isChallenge;
        setDeleteModal(null);
        try {
            await StoryService.deleteStory(currentUser?.uid || null, id);
            markAsDeleted(id);
            isChallenge ? setChallengeStories(p => p.filter(s => s.id !== id)) : setStories(p => p.filter(s => s.id !== id));
        } catch (e) { console.error('Error deleting story:', e); }
    };

    const { runPublishCheck } = useSafetyCheck();
const [isTeacher, setIsTeacher] = useState(false);
const [safetyModal, setSafetyModal] = useState<{ show: boolean; issues: SafetyIssue[] }>({ show: false, issues: [] });
const [safetyChecking, setSafetyChecking] = useState(false);

// Load isTeacher flag once on mount
useEffect(() => {
  if (!currentUser) return;
  getDoc(doc(db, 'users', currentUser.uid)).then(d => {
    setIsTeacher(d.data()?.isTeacher === true);
  });
}, [currentUser]);
 
const handlePublish = async (e: React.MouseEvent, story: StoryData) => {
  e.stopPropagation();
  if (isGuestMode) { setShowPublishGate(true); return; }
  if (!currentUser || !story.id) return;
  if (story.isChallenge) { setNotification({ show: true, type: 'error', errorMessage: '📓 Challenge stories are for practice only!' }); return; }
  if (story.isPublished) { setConfirmModal({ show: true, type: 'unpublish', story }); return; }

  // Always fetch fresh data before validating
  const freshStory = await StoryService.getStory(currentUser.uid, story.id);
  console.log('Fresh story thumbnail:', freshStory?.thumbnail);
  console.log('Fresh story content:', freshStory?.content?.chapters?.[0]);
  if (!freshStory) return;
  story = freshStory;
   
 
        const errors: string[] = [];
        if (!story.title?.trim() || story.title === 'Untitled Story') errors.push('📝 Add a title');
        if (!story.tags?.length) errors.push('🏷️ Pick at least one tag');
        const isRepublish = story.wasPublished === true;
        const hasCustomCover = (() => {
          if (!story.thumbnail) return false;
          try {
              const d = typeof story.thumbnail === 'string' ? JSON.parse(story.thumbnail) : story.thumbnail;
              if (!d?.background) return false;
              const isDefaultWhite = d.background.type === 'solid' && d.background.value === '#FFFFFF';
              return !isDefaultWhite;
          } catch { return false; }
      })();
       if (!isRepublish && !hasCustomCover) errors.push('🎨 Customize your cover');
        const wordCount = countWords(story);
        if (!isRepublish && wordCount < MIN_WORD_COUNT) errors.push(`📖 Write at least ${MIN_WORD_COUNT} words (${wordCount} now)`);
        if (stories.filter(s => s.isPublished).length >= MAX_PUBLISHED_STORIES) errors.push(`📚 Unpublish a story first (max ${MAX_PUBLISHED_STORIES})`);
        if (!isRepublish && getPublishesThisMonth(stories) >= MAX_PUBLISH_PER_MONTH) {
            const days = Math.ceil((new Date(new Date().getFullYear(), new Date().getMonth() + 1, 1).getTime() - Date.now()) / 86400000);
            errors.push(`📅 Monthly limit reached. Try again in ${days} days.`);
        }
        if (errors.length > 0) { setNotification({ show: true, type: 'error', errorMessage: errors.length === 1 ? errors[0] : `Before publishing:\n\n${errors.map((e, i) => `${i + 1}. ${e}`).join('\n')}` }); return; }
        setConfirmModal({ show: true, type: isRepublish ? 'republish' : 'publish', story });
    };
 
    const confirmPublishAction = async () => {
        const { type, story } = confirmModal;
        if (!story || !currentUser || !story.id) return;
        setConfirmModal({ show: false, type: 'publish', story: null });
        if (type === 'unpublish') {
            try {
                await StoryService.updateStory(currentUser.uid, story.id, { isPublished: false });
                const ref = doc(db, 'publishedStories', story.id);
                if ((await getDoc(ref)).exists()) await deleteDoc(ref);
                setStories(p => p.map(s => s.id === story.id ? { ...s, isPublished: false } : s));
                setNotification({ show: true, type: 'unpublish', storyTitle: story.title });
            } catch (err: any) { setNotification({ show: true, type: 'error', errorMessage: err.message || 'Failed to unpublish.' }); }
          } else {
            const isRepublish = type === 'republish';
            const wordCount = countWords(story);
            try {
                // ── Safety check (layers 1 → 3) ──────────────────────────────
                setSafetyChecking(true);
                console.log('Running safety check... isTeacher:', isTeacher);
                console.log('Story content chapters:', story.content?.chapters);
                const safetyResult = await runPublishCheck(story, isTeacher);
                console.log('Safety result:', safetyResult);
                setSafetyChecking(false);
        
                if (!safetyResult.passed) {
                    // Layer 1 or 2 failure — show modal with fixable issues
                    setSafetyModal({ show: true, issues: safetyResult.issues });
                    return;
                }
        
                if (safetyResult.needsReview) {
                    // AI flagged — queue for teacher/human review
                    let teacherId: string | null = null;
                    if (story.classId) {
                      const classDoc = await getDoc(doc(db, 'classes', story.classId as string));
                        teacherId = classDoc.data()?.teacherId || null;
                    }
                    const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
                    const userData = userDoc.data();
                    const preview = (() => {
                        const ch = story.content?.chapters?.[0];
                        const raw = ch?.blocks?.[0]?.content || ch?.content || '';
                        return raw.replace(/<[^>]*>/g, '').substring(0, 200);
                    })();
                    await setDoc(doc(db, 'moderationQueue', story.id!), {
                        storyId: story.id,
                        userId: currentUser.uid,
                        teacherId,
                        classId: story.classId || null,
                        assignmentId: story.assignmentId || null, 
                        authorName: userData?.displayName || 'Anonymous Writer',
                        storyTitle: story.title,
                        storyPreview: preview,
                        aiFlags: safetyResult.issues,
                        status: 'pending',
                        submittedAt: Timestamp.now(),
                        storyData: {
                            userId: currentUser.uid,
                            authorId: currentUser.uid,
                            authorName: userData?.displayName || 'Anonymous Writer',
                            authorPseudonym: userData?.displayName || 'Anonymous Writer',
                            authorAvatar: userData?.avatar || '✍️',
                            title: story.title,
                            type: story.type || 'regular-book',
                            description: story.description || '',
                            thumbnail: story.thumbnail,
                            content: story.content,
                            category: story.tags?.[0] || 'adventure',
                            tags: story.tags || [],
                            pageCount: story.pageCount || story.content?.chapters?.length || 0,
                            wordCount,
                            views: 0, reads: 0,
                            reactions: { heart: 0, star: 0, smile: 0, party: 0 },
                            createdAt: story.createdAt || Timestamp.now(),
                            updatedAt: Timestamp.now(),
                            isPublished: true, isFeatured: false,
                        },
                    });
                    setNotification({
                      show: true,
                      type: 'story-submitted',
                      storyTitle: story.title,
                      className: 'review',
                  });
                    return;
                }
                // ─────────────────────────────────────────────────────────────
                await StoryService.updateStory(currentUser.uid, story.id, { isPublished: true, wasPublished: true, publishedAt: Timestamp.now() });
                const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
                const userData = userDoc.data();
                await setDoc(doc(db, 'publishedStories', story.id), {
                    userId: currentUser.uid, authorId: currentUser.uid,
                    authorName: userData?.displayName || 'Anonymous Writer',
                    authorPseudonym: userData?.displayName || 'Anonymous Writer',
                    authorAvatar: userData?.avatar || '✍️',
                    title: story.title, type: story.type || 'regular-book',
                    description: story.description || story.content?.chapters?.[0]?.content?.substring(0, 150) + '...' || '',
                    thumbnail: story.thumbnail, content: story.content,
                    category: story.tags?.[0] || 'adventure', tags: story.tags || [],
                    pageCount: story.pageCount || story.content?.chapters?.length || 0,
                    wordCount, views: 0, reads: 0, reactions: { heart: 0, star: 0, smile: 0, party: 0 },
                    publishedAt: Timestamp.now(), createdAt: story.createdAt || Timestamp.now(),
                    updatedAt: Timestamp.now(), isPublished: true, isFeatured: false, moderationPassed: true,
                });
                setStories(p => p.map(s => s.id === story.id ? { ...s, isPublished: true, wasPublished: true, publishedAt: Timestamp.now() } : s));
                setNotification({ show: true, type: isRepublish ? 'republish' : 'publish', storyTitle: story.title });
            } catch (err: any) { setNotification({ show: true, type: 'error', errorMessage: err.message || 'Failed to publish.' }); }
        }
    };
 
    const handleEditTags = (e: React.MouseEvent, story: StoryData) => { e.stopPropagation(); setEditTagsModal(story); setEditingTags(story.tags || []); };
 
    const handleSaveTags = async () => {
        if (!editTagsModal?.id) return;
        try {
            await StoryService.updateStory(currentUser?.uid || null, editTagsModal.id, { tags: editingTags });
            setStories(p => p.map(s => s.id === editTagsModal.id ? { ...s, tags: editingTags } : s));
            setEditTagsModal(null); setEditingTags([]);
            setNotification({ show: true, type: 'tags', storyTitle: editTagsModal.title });
        } catch { setNotification({ show: true, type: 'error', errorMessage: 'Failed to update tags.' }); }
    };
 
    const convertChallengeToStory = async (e: React.MouseEvent, challengeStory: StoryData) => {
        e.stopPropagation();
        if (!currentUser || !challengeStory.id) return;
        try {
            const newData = { title: challengeStory.title, type: challengeStory.type || 'regular-book', content: challengeStory.content, thumbnail: challengeStory.thumbnail, tags: challengeStory.tags || [], isChallenge: false, starred: false, isPublished: false, createdAt: Timestamp.now(), lastModified: Timestamp.now() };
            const newId = await StoryService.createStory(currentUser.uid, newData as any);
            setStories(p => [{ ...newData, id: newId } as StoryData, ...p]);
            setNotification({ show: true, type: 'save', storyTitle: `"${challengeStory.title}" is now a real story!` });
        } catch { setNotification({ show: true, type: 'error', errorMessage: 'Failed to convert story.' }); }
    };
 
    // ── Filtered lists ────────────────────────────────────────────────────────
    const classStories    = stories.filter(s => s.classId && s.classId !== '');
    const personalStories = stories.filter(s => (!s.classId || s.classId === '') && !s.isChallenge);
 
    const filteredStories = activeTab === 'class'
        ? classFilter === 'all'       ? classStories
        : classFilter === 'drafts'    ? classStories.filter(s => s.status === 'draft')
        : classFilter === 'submitted' ? classStories.filter(s => s.status === 'published')
        : classFilter === 'returned'  ? classStories.filter(s => s.status === 'returned')
        : classStories
        : personalFilter === 'all'       ? personalStories
        : personalFilter === 'drafts'    ? personalStories.filter(s => !s.isPublished)
        : personalFilter === 'published' ? personalStories.filter(s => s.isPublished)
        : personalFilter === 'practice'  ? challengeStories
        : personalStories;
 
    const displayStories = filteredStories.filter(s => s.id && !isDeleted(s.id));
 
    // ── Tab/filter pill helper ────────────────────────────────────────────────
    const Pill = ({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) => (
        <button onClick={onClick} style={{ padding: '8px 18px', borderRadius: '9999px', border: 'none', background: active ? C.primary : C.surfaceHigh, color: active ? '#fff' : C.onSurfaceVariant, fontSize: '0.82rem', fontWeight: '700', cursor: 'pointer', fontFamily: "'Epilogue', sans-serif", transition: 'all 0.15s', whiteSpace: 'nowrap' }}>
            {label}
        </button>
    );
 
    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <div style={{ minHeight: '100vh', background: C.surface, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
 
            {/* ── Fixed sidebar (reuses Dashboard pattern) ─────────────────── */}
            {!isMobile && (
                <div style={{ position: 'fixed', top: 0, left: 0, bottom: 0, width: `${SIDEBAR_WIDTH}px`, background: C.surfaceLow, borderRadius: '0 24px 24px 0', padding: '80px 12px 32px', display: 'flex', flexDirection: 'column', gap: '4px', zIndex: 40, boxShadow: '4px 0 16px rgba(0,0,0,0.04)', overflowY: 'auto' }}>
                    {[
                        { icon: '🏠', label: 'Dashboard',  path: '/dashboard' },
                        { icon: '📚', label: 'My Stories', path: '/my-stories' },
                        { icon: '✏️', label: 'Write New',  path: '/write' },
                        { icon: '🃏', label: 'My Cards',   path: '/my-cards', soon: true },
                    ].map(item => {
                        const active = isActive(item.path);
                        return (
                            <button key={item.path} onClick={() => navigate(item.path)}
                                style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '10px', padding: '11px 14px', borderRadius: '9999px', border: 'none', background: active ? `linear-gradient(135deg, ${C.primary}, ${C.primaryLight})` : 'transparent', color: active ? '#fff' : C.onSurface, fontSize: '0.875rem', fontWeight: '600', fontFamily: "'Plus Jakarta Sans', sans-serif", cursor: 'pointer', textAlign: 'left', transition: 'all 0.15s' }}
                                onMouseEnter={e => { if (!active) e.currentTarget.style.background = C.surfaceHigh; }}
                                onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent'; }}
                            >
                                <span>{item.icon}</span>
                                <span style={{ flex: 1 }}>{item.label}</span>
                                {item.soon && <span style={{ background: C.tertiaryContainer, color: C.tertiary, padding: '1px 6px', borderRadius: '9999px', fontSize: '0.6rem', fontWeight: '800' }}>SOON</span>}
                            </button>
                        );
                    })}
                </div>
            )}
 
            {/* ── Main content ─────────────────────────────────────────────── */}
            <div style={{ marginLeft: isMobile ? 0 : `${SIDEBAR_WIDTH}px`, padding: isMobile ? '20px 16px 80px' : '40px 48px', boxSizing: 'border-box', overflowX: 'hidden' }}>
 
                {/* ── Page header ──────────────────────────────────────────── */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '32px', flexWrap: 'wrap', gap: '16px' }}>
                    <div>
                        <h1 style={{ fontFamily: "'Epilogue', sans-serif", fontSize: isMobile ? '1.8rem' : '2.4rem', fontWeight: '900', color: C.onBackground, margin: '0 0 4px 0' }}>
                            My Stories
                        </h1>
                        <p style={{ fontSize: '0.9rem', color: C.onSurfaceVariant, margin: 0 }}>
                            {stories.length} {stories.length === 1 ? 'story' : 'stories'} · {stories.filter(s => s.isPublished).length} published
                        </p>
                    </div>
                    <button
                        onClick={() => navigate('/write')}
                        style={{ background: `linear-gradient(135deg, ${C.primary} 0%, ${C.primaryLight} 100%)`, color: '#fff', border: 'none', borderRadius: '9999px', padding: '12px 24px', fontSize: '0.875rem', fontWeight: '700', cursor: 'pointer', fontFamily: "'Epilogue', sans-serif", boxShadow: `0 4px 14px ${C.primary}40`, transition: 'opacity 0.15s, transform 0.15s' }}
                        onMouseEnter={e => { e.currentTarget.style.opacity = '0.9'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                        onMouseLeave={e => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.transform = 'translateY(0)'; }}
                    >
                        ✨ New Story
                    </button>
                </div>
 
                {/* ── Top-level tabs ────────────────────────────────────────── */}
                <div style={{ display: 'flex', gap: '4px', borderBottom: `2px solid ${C.surfaceHigh}`, marginBottom: '20px' }}>
                    {[
                        { id: 'personal', label: `Personal (${personalStories.length + challengeStories.length})` },
                        { id: 'class',    label: `Class (${classStories.length})` },
                    ].map(tab => (
                        <button key={tab.id} onClick={() => setActiveTab(tab.id as any)}
                            style={{ padding: '12px 20px', background: 'none', border: 'none', borderBottom: `3px solid ${activeTab === tab.id ? C.primary : 'transparent'}`, color: activeTab === tab.id ? C.primary : C.onSurfaceVariant, fontSize: '0.9rem', fontWeight: '700', cursor: 'pointer', fontFamily: "'Epilogue', sans-serif", transition: 'color 0.15s' }}>
                            {tab.label}
                        </button>
                    ))}
                </div>
 
                {/* ── Sub-filter pills ──────────────────────────────────────── */}
                <div style={{ display: 'flex', gap: '8px', marginBottom: '24px', flexWrap: 'wrap' }}>
                    {activeTab === 'class' && (
                        <>
                            <Pill label="All"       active={classFilter === 'all'}       onClick={() => setClassFilter('all')} />
                            <Pill label="Drafts"    active={classFilter === 'drafts'}    onClick={() => setClassFilter('drafts')} />
                            <Pill label="Submitted" active={classFilter === 'submitted'} onClick={() => setClassFilter('submitted')} />
                            <Pill label="Returned"  active={classFilter === 'returned'}  onClick={() => setClassFilter('returned')} />
                        </>
                    )}
                    {activeTab === 'personal' && (
                        <>
                            <Pill label={`All (${personalStories.length})`}                         active={personalFilter === 'all'}       onClick={() => setPersonalFilter('all')} />
                            <Pill label={`📝 Drafts (${personalStories.filter(s => !s.isPublished).length})`}   active={personalFilter === 'drafts'}    onClick={() => setPersonalFilter('drafts')} />
                            <Pill label={`✅ Published (${personalStories.filter(s => s.isPublished).length})`} active={personalFilter === 'published'} onClick={() => setPersonalFilter('published')} />
                            <Pill label={`📓 Practice (${challengeStories.length})`}                active={personalFilter === 'practice'}  onClick={() => setPersonalFilter('practice')} />
                        </>
                    )}
                </div>
 
                {/* ── Publishing checklist (personal drafts only) ───────────── */}
                {activeTab === 'personal' && personalFilter === 'drafts' && personalStories.filter(s => !s.isPublished).length > 0 && (
                    <div style={{ background: C.secondaryContainer, borderRadius: '16px', padding: '20px 24px', marginBottom: '24px', border: `1px solid ${C.secondary}30` }}>
                        <div style={{ fontFamily: "'Epilogue', sans-serif", fontWeight: '800', fontSize: '0.95rem', color: C.secondary, marginBottom: '12px' }}>
                            ✅ Ready to publish? Make sure your story has:
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)', gap: '8px' }}>
                            {[['📝', 'A creative title'], ['🎨', 'A colorful cover'], ['🏷️', 'At least one tag'], ['📖', `${MIN_WORD_COUNT}+ words`]].map(([icon, text]) => (
                                <div key={text} style={{ background: C.surfaceLowest, borderRadius: '10px', padding: '10px 12px', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.82rem', fontWeight: '600', color: C.secondary }}>
                                    <span>{icon}</span><span>{text}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
 
                {/* ── Story grid ────────────────────────────────────────────── */}
                {loading ? (
                    <div style={{ textAlign: 'center', padding: '80px 20px', color: C.onSurfaceVariant }}>
                        <div style={{ fontSize: '3rem', marginBottom: '14px' }}>⏳</div>
                        <p style={{ fontFamily: "'Epilogue', sans-serif", fontWeight: '700', fontSize: '1rem', margin: 0 }}>Loading your stories...</p>
                    </div>
                ) : displayStories.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '80px 32px', background: C.surfaceLowest, borderRadius: '24px', border: `2px dashed ${C.outlineVariant}` }}>
                        <div style={{ fontSize: '4rem', marginBottom: '16px' }}>📚</div>
                        <h3 style={{ fontFamily: "'Epilogue', sans-serif", fontWeight: '900', fontSize: '1.3rem', color: C.onBackground, margin: '0 0 8px 0' }}>
                            {activeTab === 'class' ? 'No class stories yet!' : 'No stories here yet!'}
                        </h3>
                        <p style={{ fontSize: '0.9rem', color: C.onSurfaceVariant, margin: '0 0 20px 0' }}>
                            {activeTab === 'class' ? "Start a story linked to your class from Write New." : "Click New Story to start writing!"}
                        </p>
                        <button onClick={() => navigate('/write')}
                            style={{ background: `linear-gradient(135deg, ${C.primary}, ${C.primaryLight})`, color: '#fff', border: 'none', borderRadius: '9999px', padding: '11px 24px', fontSize: '0.875rem', fontWeight: '700', cursor: 'pointer', fontFamily: "'Epilogue', sans-serif" }}>
                            ✨ Write Something
                        </button>
                    </div>
                ) : (
                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(auto-fill, minmax(220px, 1fr))', gap: '16px' }}>
                        {displayStories.map(story => {
                            const coverData = story.thumbnail && typeof story.thumbnail === 'string'
                                ? (() => { try { return JSON.parse(story.thumbnail); } catch { return null; } })()
                                : story.thumbnail;
 
                            return (
                                <div key={story.id}
                                    onClick={() => navigate(`${getStoryRoute(story.type)}?storyId=${story.id}`)}
                                    style={{ background: C.surfaceLowest, borderRadius: '16px', overflow: 'hidden', cursor: 'pointer', border: `1px solid ${C.surfaceHigh}`, position: 'relative', transition: 'transform 0.2s, box-shadow 0.2s' }}
                                    onMouseEnter={e => { (e.currentTarget as HTMLElement).style.transform = 'translateY(-4px)'; (e.currentTarget as HTMLElement).style.boxShadow = `0 12px 32px rgba(0,0,0,0.1)`; }}
                                    onMouseLeave={e => { (e.currentTarget as HTMLElement).style.transform = 'translateY(0)'; (e.currentTarget as HTMLElement).style.boxShadow = 'none'; }}
                                >
                                    {/* Cover */}
                                    {coverData ? (
    <div style={{ width: '100%', aspectRatio: '5/8', position: 'relative', overflow: 'hidden' }}>
        <CoverRenderer coverData={coverData} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', aspectRatio: undefined }} />
        {story.isChallenge && (
            <div style={{ position: 'absolute', top: '8px', left: '8px', background: C.tertiaryContainer, color: C.tertiary, padding: '3px 10px', borderRadius: '9999px', fontSize: '0.6rem', fontWeight: '800', zIndex: 10 }}>
                📓 Practice
            </div>
        )}
        {story.isPublished && !story.isChallenge && (
            <div style={{ position: 'absolute', top: '8px', left: '8px', background: C.secondaryContainer, color: C.secondary, padding: '3px 10px', borderRadius: '9999px', fontSize: '0.6rem', fontWeight: '800', zIndex: 10 }}>
                ✅ Published
            </div>
        )}
                                            
                                        </div>
                                    ) : (
                                        <div style={{ width: '100%', aspectRatio: '5/8', background: C.surfaceLow, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '3rem', position: 'relative' }}>
                                            {story.isChallenge && (
                                                <div style={{ position: 'absolute', top: '8px', left: '8px', background: C.tertiaryContainer, color: C.tertiary, padding: '3px 10px', borderRadius: '9999px', fontSize: '0.6rem', fontWeight: '800' }}>
                                                    📓 Practice
                                                </div>
                                            )}
                                            {getStoryIcon(story.type)}
                                        </div>
                                    )}
 
                                    {/* Story info + actions */}
                                    <div style={{ padding: '12px 14px' }}>
                                        <div style={{ fontFamily: "'Epilogue', sans-serif", fontWeight: '800', fontSize: '0.88rem', color: C.onBackground, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: '3px' }}>
                                            {story.title || 'Untitled Story'}
                                        </div>
                                        <div style={{ fontSize: '0.72rem', color: C.onSurfaceVariant, marginBottom: '2px' }}>
                                            {story.content?.chapters?.length || 0} ch · {countWords(story)} words
                                        </div>
                                        <div style={{ fontSize: '0.68rem', color: C.outlineVariant, marginBottom: '10px' }}>
                                            {getTimeAgo(story.lastModified)}
                                        </div>
 
                                        {/* Action buttons row */}
                                        <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }} onClick={e => e.stopPropagation()}>
                                            {!story.isChallenge && (
                                                <>
                                                    <ActionBtn icon="🏷️" active={!!(story.tags?.length)} title="Edit tags"                                    onClick={e => handleEditTags(e, story)}                          color={C.primary} />
                                                    <ActionBtn icon={story.isPublished ? '✅' : '🌍'} active={!!story.isPublished} title={story.isPublished ? 'Unpublish' : 'Publish'} onClick={e => handlePublish(e, story)} color={C.primary} />
                                                </>
                                            )}
                                            <ActionBtn icon={story.starred ? '⭐' : '☆'} active={!!story.starred} title="Star"   onClick={e => story.id && toggleStar(e, story.id)} color={C.primary} />
                                            <ActionBtn icon="🗑️"                          active={false}           title="Delete" onClick={e => handleDelete(e, story)}               color={C.error} danger />
                                        </div>
                                    </div>
 
                                    {/* Convert button for challenge stories */}
                                    {story.isChallenge && (
                                        <button
                                            onClick={e => convertChallengeToStory(e, story)}
                                            style={{ width: '100%', padding: '10px', background: C.secondaryContainer, color: C.secondary, border: 'none', borderTop: `1px solid ${C.surfaceHigh}`, fontSize: '0.78rem', fontWeight: '700', cursor: 'pointer', fontFamily: "'Epilogue', sans-serif", display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', transition: 'all 0.15s' }}
                                            onMouseEnter={e => { e.currentTarget.style.background = C.secondary; e.currentTarget.style.color = '#fff'; }}
                                            onMouseLeave={e => { e.currentTarget.style.background = C.secondaryContainer; e.currentTarget.style.color = C.secondary; }}
                                        >
                                            🔄 Convert to Real Story
                                        </button>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
 
            {/* ── Delete modal ──────────────────────────────────────────────── */}
            {deleteModal && (
                <div onClick={() => setDeleteModal(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(37,47,60,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px', backdropFilter: 'blur(4px)' }}>
                    <div onClick={e => e.stopPropagation()} style={{ background: C.surfaceLowest, borderRadius: '24px', padding: '36px', maxWidth: '380px', width: '100%', textAlign: 'center', boxShadow: '0 24px 64px rgba(0,0,0,0.2)', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                        <div style={{ fontSize: '3rem', marginBottom: '14px' }}>🗑️</div>
                        <h3 style={{ fontFamily: "'Epilogue', sans-serif", fontWeight: '900', fontSize: '1.2rem', color: C.onBackground, margin: '0 0 8px 0' }}>Delete "{deleteModal.title}"?</h3>
                        <p style={{ fontSize: '0.9rem', color: C.onSurfaceVariant, margin: '0 0 28px 0' }}>This story will be gone forever.</p>
                        <div style={{ display: 'flex', gap: '12px' }}>
                            <button onClick={() => setDeleteModal(null)} style={{ flex: 1, padding: '12px', borderRadius: '9999px', border: `1.5px solid ${C.outlineVariant}`, background: C.surfaceLowest, fontSize: '0.875rem', fontWeight: '700', cursor: 'pointer', color: C.onSurfaceVariant, fontFamily: "'Epilogue', sans-serif" }}>Cancel</button>
                            <button onClick={confirmDelete} style={{ flex: 1, padding: '12px', borderRadius: '9999px', border: 'none', background: C.error, color: '#fff', fontSize: '0.875rem', fontWeight: '700', cursor: 'pointer', fontFamily: "'Epilogue', sans-serif" }}>Delete</button>
                        </div>
                    </div>
                </div>
            )}
 
            {/* ── Edit tags modal ───────────────────────────────────────────── */}
            {editTagsModal && (
                <div onClick={() => { setEditTagsModal(null); setEditingTags([]); }} style={{ position: 'fixed', inset: 0, background: 'rgba(37,47,60,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px', backdropFilter: 'blur(4px)' }}>
                    <div onClick={e => e.stopPropagation()} style={{ background: C.surfaceLowest, borderRadius: '24px', padding: '36px', maxWidth: '500px', width: '100%', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 24px 64px rgba(0,0,0,0.2)', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                        <h2 style={{ fontFamily: "'Epilogue', sans-serif", fontWeight: '900', fontSize: '1.4rem', color: C.onBackground, margin: '0 0 4px 0' }}>🏷️ Edit Tags</h2>
                        <p style={{ fontSize: '0.875rem', color: C.onSurfaceVariant, margin: '0 0 24px 0' }}>Pick tags to help readers find "{editTagsModal.title}"</p>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '10px', marginBottom: '28px' }}>
                            {availableTags.map(tag => (
                                <button key={tag} onClick={() => setEditingTags(editingTags.includes(tag) ? editingTags.filter(t => t !== tag) : [...editingTags, tag])}
                                    style={{ padding: '10px 12px', border: `2px solid ${editingTags.includes(tag) ? C.primary : C.outlineVariant}`, borderRadius: '10px', background: editingTags.includes(tag) ? C.primaryContainer : C.surfaceLowest, fontSize: '0.875rem', fontWeight: '600', cursor: 'pointer', color: editingTags.includes(tag) ? C.primary : C.onSurface, transition: 'all 0.15s' }}>
                                    {tag}
                                </button>
                            ))}
                        </div>
                        <div style={{ display: 'flex', gap: '12px' }}>
                            <button onClick={() => { setEditTagsModal(null); setEditingTags([]); }} style={{ flex: 1, padding: '12px', borderRadius: '9999px', border: `1.5px solid ${C.outlineVariant}`, background: C.surfaceLowest, fontSize: '0.875rem', fontWeight: '700', cursor: 'pointer', color: C.onSurfaceVariant, fontFamily: "'Epilogue', sans-serif" }}>Cancel</button>
                            <button onClick={handleSaveTags} style={{ flex: 1, padding: '12px', borderRadius: '9999px', border: 'none', background: `linear-gradient(135deg, ${C.primary}, ${C.primaryLight})`, color: '#fff', fontSize: '0.875rem', fontWeight: '700', cursor: 'pointer', fontFamily: "'Epilogue', sans-serif" }}>Save Tags</button>
                        </div>
                    </div>
                </div>
            )}
 
            {/* ── Confirm publish/unpublish modal ───────────────────────────── */}
            {confirmModal.show && (
                <div onClick={() => setConfirmModal({ show: false, type: 'publish', story: null })} style={{ position: 'fixed', inset: 0, background: 'rgba(37,47,60,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px', backdropFilter: 'blur(4px)' }}>
                    <div onClick={e => e.stopPropagation()} style={{ background: C.surfaceLowest, borderRadius: '24px', padding: '36px', maxWidth: '380px', width: '100%', textAlign: 'center', boxShadow: '0 24px 64px rgba(0,0,0,0.2)', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                        <div style={{ fontSize: '3rem', marginBottom: '14px' }}>{confirmModal.type === 'unpublish' ? '📦' : '🌍'}</div>
                        <h3 style={{ fontFamily: "'Epilogue', sans-serif", fontWeight: '900', fontSize: '1.2rem', color: C.onBackground, margin: '0 0 8px 0' }}>
                            {confirmModal.type === 'unpublish' ? 'Unpublish' : confirmModal.type === 'republish' ? 'Republish' : 'Publish'} "{confirmModal.story?.title}"?
                        </h3>
                        <p style={{ fontSize: '0.875rem', color: C.onSurfaceVariant, margin: '0 0 28px 0', lineHeight: '1.6' }}>
                            {confirmModal.type === 'unpublish' ? 'It will be removed from Featured Stories.' : confirmModal.type === 'republish' ? '✨ Your story will appear on Featured Stories again!' : "✅ We'll check it for safety, then everyone can read it!"}
                        </p>
                        <div style={{ display: 'flex', gap: '12px' }}>
                            <button onClick={() => setConfirmModal({ show: false, type: 'publish', story: null })} style={{ flex: 1, padding: '12px', borderRadius: '9999px', border: `1.5px solid ${C.outlineVariant}`, background: C.surfaceLowest, fontSize: '0.875rem', fontWeight: '700', cursor: 'pointer', color: C.onSurfaceVariant, fontFamily: "'Epilogue', sans-serif" }}>Cancel</button>
                            <button onClick={confirmPublishAction} disabled={safetyChecking} 
                                style={{ flex: 1, padding: '12px', borderRadius: '9999px', border: 'none', 
                                background: confirmModal.type === 'unpublish' ? C.tertiary : `linear-gradient(135deg, ${C.primary}, ${C.primaryLight})`, 
                                color: '#fff', fontSize: '0.875rem', fontWeight: '700', cursor: 'pointer', 
                                fontFamily: "'Epilogue', sans-serif",
                                opacity: safetyChecking ? 0.7 : 1  }}
                                >
                                {safetyChecking ? '🔍 Checking…' : confirmModal.type === 'unpublish' ? 'Unpublish' : 'Publish'}
                                </button>
                        </div>
                    </div>
                </div>
            )}
 
            <Notification isOpen={notification.show} onClose={() => setNotification({ ...notification, show: false })} type={notification.type} storyTitle={notification.storyTitle} errorMessage={notification.errorMessage} />
 
            {showPublishGate && <RegistrationGate type="publish" draftTitle="Your Story" onRegister={() => navigate('/signup')} onCancel={() => setShowPublishGate(false)} />}
            {safetyModal.show && (
      <SafetyFeedbackModal
          issues={safetyModal.issues}
          onClose={() => setSafetyModal({ show: false, issues: [] })}
      />
  )}
        </div>
    );
   
};
 

// ── Small action button used on story cards ────────────────────────────────────
const ActionBtn: React.FC<{ icon: string; active: boolean; title: string; onClick: (e: React.MouseEvent) => void; color: string; danger?: boolean }> = ({ icon, active, title, onClick, color, danger }) => (
    <button
        onClick={onClick}
        title={title}
        style={{ width: '30px', height: '30px', borderRadius: '8px', border: `1.5px solid ${danger ? '#ffd9df' : active ? color : '#d6e3f9'}`, background: active ? (danger ? '#ffd9df' : color) : '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', boxShadow: '0 2px 6px rgba(0,0,0,0.1)', transition: 'all 0.15s' }}
        onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.15)'; if (danger) { e.currentTarget.style.background = '#b41340'; e.currentTarget.style.borderColor = '#b41340'; } }}
        onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; if (danger) { e.currentTarget.style.background = '#ffd9df'; e.currentTarget.style.borderColor = '#ffd9df'; } }}
    >
        {icon}
    </button>
);
 
export default MyStories;










